import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";

import {
  Observable,
  Subject,
  pipe,
  combineLatest,
  EMPTY,
  catchError,
  take,
  skip,
  of,
  map,
  tap,
  takeUntil,
  switchMap,
  ReplaySubject,
  from,
  takeWhile,
  throwError,
  BehaviorSubject,
  delay
} from "rxjs";
import jwtDecode from "jwt-decode";
import { Intercom } from "ng-intercom";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import { AngularFireAuth } from "@angular/fire/compat/auth";

import { environment } from "../../../../../environments/environment";
import { AccountService } from "app/core/services/account/account.service";
import { UserProfileService } from "app/core/services/user-profile/user-profile.service";
import { RosterService } from "../../roster/roster.service";
import { VERSION } from "../../../../../environments/version";
import { LaunchDarklyService } from "app/launchdarkly.service";
import { DEFAULT_AFTER_LOGIN } from "app/core/config/app.config";

@Injectable({
  providedIn: "root"
})
export class LoginService {
  token;
  status;
  currentAccount;
  currentUser;
  version: string;
  errors = new Subject<any>();
  endPoint = environment.apiUrlAuth;
  tokenData = new ReplaySubject<any>(1);
  // _tokenData: any = {};
  stopSignal$ = new Subject();
  loggedOutSignal$ = new Subject();
  tenant = environment.tenant;
  backDrop = new BehaviorSubject<boolean>(false);

  refreshing = false;
  private isLoggingOut = false;

  local = environment.e2e;

  constructor(
    private http: HttpClient,
    private router: Router,
    public intercom: Intercom,
    private accountService: AccountService,
    private userProfileService: UserProfileService,
    private rosterService: RosterService,
    public fireAuth: AngularFireAuth,
    private ldService: LaunchDarklyService,
    private translate: TranslateService
  ) {
    this.version = `${VERSION.version}.${VERSION.hash}`;
  }

  signIn(userData, tenant = "taskezy"): Observable<any> {
    this.stopSignal$ = new Subject();
    this.loggedOutSignal$ = new Subject();
    // this.stopLoginSignal$ = new Subject();
    if (this.local) {
      return this.http.get(`api/login/${encodeURI(userData.password)}`).pipe(
        tap((token: any) => {
          this.postLoginValidationChecks(token);
        }),
        catchError((err) => {
          console.log(err);
          return EMPTY;
        }),
        takeUntil(this.stopSignal$),
        map((token) => {
          token.token = token.id;
          return token;
        }),
        this.loginPipe()
      );
    } else {
      return this.http.post(`${this.endPoint}/login/${tenant}`, userData).pipe(
        tap((token: any) => {
          console.log(token);
          this.postLoginValidationChecks(token);
        }),
        takeUntil(this.stopSignal$),
        this.loginPipe()
      );
    }
  }

  /**
   * Verify user's current password without triggering full login pipeline.
   * Reuses the login endpoint but only checks credentials — no token storage,
   * no account loading, no routing.
   *
   * MOCK: Remove when BE for TEZY2-4200 is ready
   */
  verifyPassword(
    password: string,
    tenant: string = "taskezy"
  ): Observable<boolean> {
    // MOCK: Remove when BE for TEZY2-4200 is ready
    if (this.local) {
      if (password === "wrong") {
        return throwError(() => ({
          status: 401,
          error: { message: "Invalid credentials" }
        }));
      }
      return of(true).pipe(delay(500));
    }

    let email = localStorage.getItem("email");
    if (!email) {
      // Fallback: extract email from stored JWT token
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          email = payload.email || payload.sub;
        } catch (e) {
          // token decode failed
        }
      }
    }
    if (!email) {
      return throwError(() => new Error("No email found in session"));
    }

    return this.http
      .post(`${this.endPoint}/login/${tenant}`, {
        loginId: email,
        password
      })
      .pipe(map(() => true));
  }

  setRememberMe(val, email?) {
    localStorage.setItem("rememberMe", val.toString());
    if (val) {
      localStorage.setItem("email", email);
    } else {
      localStorage.removeItem("email");
    }
  }

  postLoginValidationChecks(token) {
    // Only overwrite refreshToken if the response actually provides one.
    // The backend uses "Reusable" refresh tokens — the /refresh endpoint
    // may return the same value or omit it entirely.
    if (token.refreshToken) {
      localStorage.setItem("refreshToken", token.refreshToken);
    }
    if (token.token) {
      localStorage.setItem("token", token.token);
    }
    if (token.user?.id) {
      localStorage.setItem("user_id", token.user.id);
    }
    // localStorage.setItem('token', token.token);
    const tokenData = this.getTokenData(token.token);
    if (token.statusCode === 212) {
      this.router.navigate(["/auth/verify-email"]);
      this.stopSignal$.next(true);
    } else {
      // localStorage.setItem('token', token.token);
      this.accountService.getAllAccounts().subscribe();
      if (!tokenData.email_verified) {
        this.router.navigate(["/auth/verify-email"]);
        this.stopSignal$.next(true);
      } else if (!tokenData.mobile_verified) {
        this.router.navigate(["/auth/verify-mobile"]);
        this.stopSignal$.next(true);
      }
      // else if (tokenData.profile_verified < 2) {
      //   this.router.navigate(["/auth/id-check"]);
      //   this.stopSignal$.next(true);
      // } else
      {
        // Fully Verified
        this.token = token.token;
        // localStorage.setItem('refreshToken', token.refreshToken);
        // localStorage.setItem('token', token.token);
        // localStorage.setItem('user_id', token.user.id);
        // localStorage.setItem('email', tokenData.email);
      }
    }
    return tokenData;
  }

  getInfoByEmail(email, tenant = "taskezy") {
    if (this.local) {
      return of({ id: "", mobileVerified: false, profileVerified: 0 });
    }
    return this.http.get(`${this.endPoint}/user/${email}/${tenant}`);
  }

  getUser() {
    return this.http.get(`${this.endPoint}/user`);
  }

  refreshLoginToken(body): Observable<any> {
    this.refreshing = true;
    this.stopSignal$ = new Subject();
    this.loggedOutSignal$ = new Subject();
    // this.stopLoginSignal$ = new Subject();
    if (this.local) {
      return this.http.get(`api/login/${encodeURI(body.refreshToken)}`).pipe(
        tap((token: any) => {
          this.postLoginValidationChecks(token);
        }),
        takeUntil(this.stopSignal$),
        map((token) => {
          token.token = token.id;
          return token;
        }),
        this.loginPipe()
        // login pipe?
      );
    } else {
      return this.http.post(`${this.endPoint}/refresh`, body).pipe(
        tap((token: any) => {
          this.postLoginValidationChecks(token);
        }),
        catchError((error) => {
          // Catch HTTP errors BEFORE loginPipe() runs to prevent partial state
          console.error("Refresh token HTTP error:", error);
          this.refreshing = false;
          return throwError(() => error);
        }),
        takeUntil(this.stopSignal$),
        this.loginPipe()
      );
    }
  }

  getTokenData(token) {
    const tokenData = JSON.parse(atob(token.split(".")[1]));
    this.tokenData.next(tokenData);
    return tokenData;
  }
  // verifyToken(token): Observable<any> {
  //   return this.http.post(`${this.endPoint}/jwt/validate`, token);
  // }

  signOut() {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    this.loggedOutSignal$.next(true);
    this.refreshing = false;
    // this should call the logout API
    const refreshToken = localStorage.getItem("refreshToken");
    if (!environment.e2e) {
      this.intercom.shutdown();
      this.intercom.boot({
        app_id: environment.intercomAPIKey,
        widget: {
          activator: "#intercom"
        },
        version: this.version,
        language_override: this.translate.currentLang
      });
      this.ldService.identify({
        kind: "multi",
        user: { key: "anon", anonymous: true }
      });
    }
    // this.stopLoginSignal$.next(true);
    if (this.local) {
      this.http
        .get(`api/login/${encodeURI(refreshToken)}`)
        .pipe(
          tap((res) => {
            this.removeToken();
            this.cleanUpData();
            this.router.navigate(["/auth/login"]);
          }),
          catchError((err) => {
            console.log(err);
            this.router.navigate(["/auth/login"]);
            this.removeToken();
            this.cleanUpData();
            return EMPTY;
          })
        )
        .subscribe();
    } else {
      if (!refreshToken) {
        this.logOutWrapper();
        this.isLoggingOut = false;
        return;
      }
      const isRosterezy = this.ldService.getFlag("is-rosterezy", true);
      const tenant = isRosterezy ? "rosterezy" : "taskezy";
      const formData: any = new FormData();
      formData.append("refreshToken", refreshToken);
      this.http
        .post(`${this.endPoint}/logout/${tenant}`, formData)
        .pipe(
          tap((res) => {
            this.logOutWrapper();
          }),
          catchError((err) => {
            this.logOutWrapper();
            return EMPTY;
          })
        )
        .subscribe({
          complete: () => {
            this.isLoggingOut = false;
          },
          error: () => {
            this.isLoggingOut = false;
          }
        });
    }
  }

  getToken(): string {
    this.token = localStorage.getItem("token");
    return this.token;
  }

  getTokenExpirationDate(token: string): Date {
    try {
      const decoded: any = jwtDecode(token, { header: true });
      if (!decoded.exp) {
        return null;
      }

      const date = new Date(0);
      date.setUTCSeconds(decoded.exp);
      return date;
    } catch (e) {
      return null;
    }
  }

  authCheck() {
    return !!this.getToken();
  }

  isAuthenticated(): Observable<any> {
    const checkAuth = !!this.getToken();
    if (checkAuth) {
      return combineLatest([
        this.tokenData,
        this.accountService.accountsCount
      ]).pipe(
        map((res: any) => {
          console.log(res);
          return {
            isAuth: checkAuth,
            accountCount: res[1],
            emailVerified: res[0].email_verified,
            mobileVerified: res[0].mobile_verified,
            profileVerified: res[0].profile_verified
          };
        })
      );
    } else {
      return of({
        isAuth: checkAuth
      });
    }
  }

  loginPipe = () =>
    pipe(
      switchMap((res: any) => {
        console.log(res);
        this.userProfileService.currentUser = this.currentUser = res.user;

        const intercomData = {
          app_id: environment.intercomAPIKey,
          widget: {
            activator: "#intercom"
          },
          version: this.version,
          language_override: this.translate.currentLang
        };

        if (this.currentUser.id) {
          intercomData["email"] = this.currentUser.email;
          intercomData["user_id"] = this.currentUser.id;
          if (!environment.e2e) {
            this.intercom.boot(intercomData);
            Sentry.setUser({
              id: this.currentUser.id,
              email: this.currentUser.email
            });
            this.ldService.identify({
              kind: "multi",
              user: { key: this.currentUser.id, email: this.currentUser.email }
            });
          }
        } else {
          intercomData["email"] = "";
          intercomData["user_id"] = "";
          if (!environment.e2e) {
            this.intercom.boot(intercomData);
            Sentry.setUser({
              id: "",
              email: ""
            });
            this.ldService.identify({
              kind: "multi",
              user: { key: "anon", anonymous: true }
            });
          }
          localStorage.removeItem("user_id");
        }

        return this.accountService.listAccounts;
      }),
      // skip(1),
      // takeWhile((res) => res[0].id),
      takeUntil(this.loggedOutSignal$),
      switchMap((resList: any) => {
        // const userProfile = this.userProfileService.currentUser.getValue();
        // if (!userProfile.id) {
        //   return EMPTY;
        // }
        const accounts = resList;

        if (this.currentUser.id) {
          const currAccnt = localStorage.getItem("currentAccount");
          if (currAccnt) {
            return this.accountService.setCurrentAccount(currAccnt);
          } else {
            if (
              this.currentUser.data[this.tenant] &&
              this.currentUser.data[this.tenant].defaultAccount &&
              this.currentUser.data[this.tenant].defaultAccount !== "" &&
              accounts.filter(
                (account) =>
                  account.uuid ===
                  this.currentUser.data[this.tenant].defaultAccount
              ).length > 0
            ) {
              return this.accountService.setCurrentAccount(
                this.currentUser.data[this.tenant].defaultAccount
              );
            } else {
              if (accounts.length > 0) {
                return this.accountService.setCurrentAccount(accounts[0].uuid);
              } else {
                return of({});
              }
            }
          }
        } else {
          return of({});
        }
      }),
      // This was commented out because setCurrentAccount already calls all these requests.

      // switchMap((res: any) => {
      //   if (res && res.uuid) {
      //     this.currentAccount = res;
      //     return combineLatest([this.accountService.getConnectedVenues(this.currentAccount?.uuid),
      //       this.accountService.getAdmins(this.currentAccount?.uuid),
      //       this.accountService.getConnectedResources(this.currentAccount?.uuid)]).pipe(take(1));
      //   }
      //     return of([]);
      // }),
      tap((res: any) => {
        this.currentAccount = res;
        this.accountService.getMyAccountInvites();
        if (this.currentAccount?.uuid) {
          if (!this.refreshing) {
            this.router.navigate([DEFAULT_AFTER_LOGIN]);
          }
        } else {
          if (
            !this.currentUser.birthDate ||
            !this.currentUser.data ||
            !this.currentUser.data.address
          ) {
            this.router.navigate(["/download"]);
          } else {
            this.router.navigate(["/welcome"]);
          }
          // TODO: Force the user to go to the create primary, accept invites etc page.
          console.log("this user doesnt have any accounts linked to them");
        }
      })
    );

  loginToFirebase() {
    if (this.token) {
      return this.http
        .post(`${this.endPoint}/jwt/firebase`, { token: this.token })
        .pipe(
          switchMap((fbToken: any) => {
            if (fbToken.statusCode === 200) {
              return from(this.fireAuth.signInWithCustomToken(fbToken.token));
            } else {
              return EMPTY;
            }
          }),
          tap(
            (success) => {
              console.log(success);
            },
            (error) => {
              console.log(error);
            }
          )
        );
    } else {
      return EMPTY;
    }
  }

  logOutWrapper(): void {
    this.router.navigate(["/auth/login"]);
    this.removeToken();
    this.cleanUpData();
    this.isLoggingOut = false;
  }

  private removeToken(): void {
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("currentAccount");
    localStorage.removeItem("user");
  }

  private cleanUpData(): void {
      this.accountService?.cleanUp?.();
      this.userProfileService?.cleanUp?.();
      this.rosterService?.cleanUp?.();
      this.accountService?.cleanUp?.();
  }
}
